const { sleep, to32Bytes, log, bytesToHex } = require('./utils.js')
const { consumeInterWorkerMessage } = require('./api')
const { getPublicKey } = require('./crypto')
const Executor = require('./executor')
const { MESSAGE_TYPES, TIMING_CONSTANTS, TRANSPORT_MODES, LOCAL_CALL_TARGET } = require('./constants')

/**
 * @typedef {import('./types').ClientOptions} ClientOptions
 * @typedef {import('./types').WorkersChannelClient} WorkersChannelClient
 */

/**
 * Detects and returns the appropriate WebSocket implementation
 *
 * Supports both browser (WebSocket/MozWebSocket) and Node.js (ws package).
 *
 * @returns {Function} WebSocket constructor
 */
const getWebSocketImplementation = () => {
  // Browser environment
  if (typeof global.WebSocket !== 'undefined') {
    return global.WebSocket
  }

  // Legacy Firefox
  if (typeof global.MozWebSocket !== 'undefined') {
    return global.MozWebSocket
  }

  // Node.js environment - require 'ws' package
  try {
    return require('ws')
  } catch (err) {
    throw new Error(
      'WebSocket implementation not found. In Node.js, install the "ws" package.',
    )
  }
}

/**
 * Creates a new Workers Channel client
 *
 * The client provides:
 * 1. WebSocket connection for real-time message delivery (when callTransport requires it)
 * 2. HTTP polling for offline message retrieval (when WebSocket is enabled)
 * 3. RPC call() method for invoking methods on other workers
 *
 * Transport Modes:
 * - 'http': Pure HTTP mode, no WebSocket connection
 *   - Ideal for CLI tools, test scripts, or clients that only send requests
 *   - Cannot have sandbox methods (will throw error)
 *   - Does not require websocketUrl
 *
 * - 'ws': WebSocket mode, requires persistent WebSocket connection
 *   - Ideal for long-running workers that send/receive calls
 *   - Requires websocketUrl
 *   - Can have sandbox methods to receive calls from other workers
 *
 * - 'auto': Automatic mode (default)
 *   - Uses WebSocket if sandbox has methods
 *   - Falls back to HTTP if no sandbox methods
 *
 * Configuration Validation:
 * - 'http' mode with sandbox methods → ERROR (will cause response timeout)
 * - 'ws' mode without websocketUrl → ERROR (cannot establish connection)
 * - 'auto' mode with sandbox but no websocketUrl → ERROR (cannot receive calls)
 *
 * @param {ClientOptions} options - Client configuration
 * @returns {WorkersChannelClient} Client instance
 */
const newWorkersChannelClient = ({
  serviceUrl,
  websocketUrl,
  privateKey,
  sandbox = {},
  consumeInterval = TIMING_CONSTANTS.DEFAULT_CONSUME_INTERVAL,
  callTimeout = TIMING_CONSTANTS.DEFAULT_CALL_TIMEOUT,
  callTransport = TRANSPORT_MODES.AUTO, // Default to AUTO for safer behavior
  namespaceClient, // Optional namespace client for callByName
}) => {
  // Normalize private key to 32 bytes
  const privKey = to32Bytes(privateKey)

  // Derive public key from private key
  const publicKey = getPublicKey(privKey)

  // Validate configuration
  // Check if sandbox has any function methods (ignore non-function values)
  const { flattenSandbox } = require('./utils')
  const flatSandbox = flattenSandbox(sandbox)
  const hasSandboxMethods = Object.keys(flatSandbox).length > 0

  // Note: HTTP mode with sandbox methods is allowed.
  // Sandbox methods are accessible via local calls (call('local', ...)) without WebSocket.
  // Remote workers will NOT be able to call this worker's sandbox methods in HTTP-only mode.

  // Determine if WebSocket is needed based on callTransport
  let needsWebSocket = false
  let effectiveTransport = callTransport

  switch (callTransport) {
    case TRANSPORT_MODES.HTTP:
      needsWebSocket = false
      effectiveTransport = TRANSPORT_MODES.HTTP
      break

    case TRANSPORT_MODES.WS:
      needsWebSocket = true
      effectiveTransport = TRANSPORT_MODES.WS
      if (!websocketUrl) {
        throw new Error(
          'Configuration error: callTransport="ws" requires websocketUrl to be provided.'
        )
      }
      break

    case TRANSPORT_MODES.AUTO:
      needsWebSocket = hasSandboxMethods && !!websocketUrl
      effectiveTransport = needsWebSocket ? TRANSPORT_MODES.WS : TRANSPORT_MODES.HTTP
      break

    default:
      throw new Error(
        `Invalid callTransport value: "${callTransport}". ` +
          `Must be one of: "http", "ws", or "auto".`
      )
  }

  // Track client lifecycle state
  let wsConnected = false
  let closed = false

  // Create RPC executor
  const { call, callSandboxMethod, processInterWorkerMessage } = Executor({
    sandbox,
    serviceUrl,
    privKey,
    publicKey,
    callTimeout,
    transport: effectiveTransport, // Use effectiveTransport (resolved from callTransport)
    wsConnected: () => wsConnected, // Pass as function to get current state
  })

  log(`starting worker ${bytesToHex(publicKey)}`)

  /**
   * Sends a JSON message over WebSocket
   *
   * @param {WebSocket} conn - WebSocket connection
   * @param {Object} json - JSON object to send
   * @param {Function} onError - Error callback
   */
  const sendJson = (conn, json, onError) => {
    try {
      conn.send(JSON.stringify(json))
    } catch (err) {
      onError(err)
    }
  }

  /**
   * Sends WebSocket keepalive pings
   *
   * Sends empty string every interval to keep connection alive.
   * The server has a 60s read deadline.
   *
   * @param {WebSocket} conn - WebSocket connection
   * @param {Function} onError - Error callback
   * @returns {number} Timer ID for cleanup
   */
  const startKeepAlive = (conn, onError) => {
    const sendPing = () => {
      try {
        conn.send('') // Empty string as ping
      } catch (err) {
        onError(err)
      }
    }

    // Send ping immediately, then repeat
    sendPing()

    return setInterval(sendPing, TIMING_CONSTANTS.WEBSOCKET_KEEPALIVE_INTERVAL)
  }

  /**
   * Handles incoming WebSocket messages
   *
   * @param {string} data - Raw message data
   */
  const handleWebSocketMessage = async (data) => {
    try {
      const { message_type, worker_request } = JSON.parse(data)

      if (message_type === MESSAGE_TYPES.MESSAGE) {
        await processInterWorkerMessage(worker_request)
      }
    } catch (err) {
      log('error handling WebSocket message:', err)
    }
  }

  /**
   * Establishes a WebSocket connection with auto-reconnect
   *
   * Process:
   * 1. Create WebSocket connection
   * 2. Send initial handshake with public key
   * 3. Start keepalive pings
   * 4. Handle incoming messages
   * 5. On disconnect, resolve Promise to trigger reconnect
   *
   * @returns {Promise<void>} Resolves when connection closes
   */
  const connectWS = () => {
    log('connecting to WebSocket server:', websocketUrl)

    return new Promise((resolve) => {
      let conn = null
      let keepAliveTimer = null
      let hasResolved = false

      /**
       * Cleans up and resolves the Promise
       * Called on error, close, or other termination
       */
      const cleanup = (err) => {
        // Prevent multiple cleanups
        if (hasResolved) return
        hasResolved = true

        // Update connection state
        wsConnected = false

        if (err) {
          log('WebSocket error:', err.message)
        }

        // Close connection if still open
        if (conn !== null) {
          try {
            conn.close()
          } catch (e) {
            // Ignore close errors
          }
        }

        // Stop keepalive timer
        if (keepAliveTimer !== null) {
          clearInterval(keepAliveTimer)
        }

        // Resolve to allow reconnect
        resolve()
      }

      try {
        // Get WebSocket implementation
        const WebSocket = getWebSocketImplementation()

        // Create connection
        conn = new WebSocket(websocketUrl)

        // Connection opened - send handshake
        conn.onopen = () => {
          log('WebSocket connected')
          wsConnected = true // Update connection state

          // Send public key in handshake
          sendJson(
            conn,
            {
              public_key: bytesToHex(publicKey),
            },
            cleanup,
          )

          // Start keepalive pings
          keepAliveTimer = startKeepAlive(conn, cleanup)
        }

        // Message received
        conn.onmessage = async ({ data }) => {
          await handleWebSocketMessage(data)
        }

        // Connection error
        conn.onerror = (e) => {
          cleanup(new Error(`WebSocket error: ${e.message || 'unknown'}`))
        }

        // Connection closed
        conn.onclose = (e) => {
          cleanup(
            new Error(
              `WebSocket closed [code=${e.code}] ${e.reason || 'no reason'}`,
            ),
          )
        }
      } catch (err) {
        cleanup(err)
      }
    })
  }

  /**
   * Continuously maintains WebSocket connection
   *
   * Automatically reconnects after disconnections.
   */
  const consumeWSMessages = async () => {
    while (!closed) {
      await connectWS()
      if (closed) break

      // Wait before reconnecting
      log('WebSocket disconnected, reconnecting...')
      await sleep(TIMING_CONSTANTS.WEBSOCKET_RECONNECT_DELAY)
    }
  }

  /**
   * Continuously polls for offline messages
   *
   * Retrieves messages that were queued while the worker was offline.
   * Runs in parallel with WebSocket connection.
   */
  const keepConsumeInterWorkerMessage = async () => {
    while (!closed) {
      try {
        // Poll for pending messages
        const { requests } = await consumeInterWorkerMessage({
          publicKey,
          privKey,
          serviceUrl,
          payload: {
            message_type: MESSAGE_TYPES.REQUEST,
          },
        })

        // Process all pending messages
        if (requests && requests.length > 0) {
          log(`received ${requests.length} offline messages`)

          // Process messages sequentially to avoid overwhelming the worker
          // TODO: Consider parallel processing with concurrency limit
          for (const request of requests) {
            try {
              await processInterWorkerMessage(request)
            } catch (err) {
              log('error processing offline message:', err)
            }
          }
        }
      } catch (err) {
        log('error polling for messages:', err)
      }

      // Wait before next poll
      await sleep(consumeInterval)
    }
  }

  // Start background tasks if needed
  if (needsWebSocket) {
    const reasons = []
    if (hasSandboxMethods) {
      reasons.push('sandbox has methods')
    }
    reasons.push(`effectiveTransport=${effectiveTransport}`)
    log(`starting WebSocket connection and message polling (${reasons.join(', ')})`)
    consumeWSMessages()
    keepConsumeInterWorkerMessage()
  } else {
    log(`pure HTTP mode (callTransport=${callTransport}, no sandbox, no WebSocket)`)
  }

  /**
   * Makes an RPC call to another worker by name
   *
   * Requires namespaceClient to be provided in constructor options.
   * Resolves worker name to public key, then makes the RPC call.
   *
   * @param {string} workerName - Worker name to call
   * @param {string} method - Method name to call
   * @param {Array<*>} params - Method parameters
   * @param {CallOptions} opts - Call options
   * @returns {Promise<*>} Response data
   * @throws {Error} If namespaceClient is not configured
   */
  const callByName = async (workerName, method, params, opts) => {
    if (workerName === LOCAL_CALL_TARGET) {
      return call(LOCAL_CALL_TARGET, method, params, opts)
    }

    if (!namespaceClient) {
      throw new Error(
        'callByName requires namespaceClient to be provided in client options. ' +
          'Pass a namespaceClient with getPublicKey(name) method when creating the client.',
      )
    }

    // Resolve worker name to public key
    const resolvedKey = await namespaceClient.getPublicKey(workerName)

    // Make the RPC call with the resolved public key
    try {
      return await call(resolvedKey, method, params, opts)
    } catch (err) {
      err.workerName = workerName
      err.message = `[${workerName}] ${err.message}`
      throw err
    }
  }

  // Return public API
  return {
    /**
     * Makes an RPC call to another worker
     *
     * @param {string} receiverPublicKey - Receiver's public key (hex)
     * @param {string} method - Method name to call
     * @param {Array<*>} params - Method parameters
     * @param {CallOptions} opts - Call options
     * @returns {Promise<*>} Response data
     */
    call,

    /**
     * Makes an RPC call to another worker by name
     *
     * Requires namespaceClient to be provided in constructor options.
     *
     * @param {string} workerName - Worker name to call
     * @param {string} method - Method name to call
     * @param {Array<*>} params - Method parameters
     * @param {CallOptions} opts - Call options
     * @returns {Promise<*>} Response data
     * @throws {Error} If namespaceClient is not configured
     */
    callByName,

    /**
     * Calls a method in the local sandbox
     *
     * @param {RPCContext} ctx - RPC context
     * @param {string} method - Method name
     * @param {Array<*>} params - Method parameters
     * @returns {Promise<*>} Method result
     */
    callSandboxMethod,

    /**
     * This worker's Ed25519 public key
     * @type {Uint8Array}
     */
    publicKey,

    /**
     * Stops background loops (WebSocket reconnect and message polling)
     */
    close() {
      closed = true
    },
  }
}

module.exports = {
  newWorkersChannelClient,
}
